import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from './components/Navbar/Navbar';
import { Hero } from './components/Hero/Hero';
import { ParticleBackground } from './components/ParticleBackground';
import { ExperienceSection } from './components/ExperienceSection/ExperienceSection';
import { Features } from './components/Features/Features';
import { ExtensionShowcase } from './components/ExtensionShowcase/ExtensionShowcase';
import { HowItWorks } from './components/HowItWorks/HowItWorks';
import { Testimonials } from './components/Testimonials/Testimonials';
import { BlogSection } from './components/Blog/BlogSection';
import { FAQ } from './components/FAQ/FAQ';
import { Footer } from './components/Footer/Footer';
import { ScrollTracker } from './components/common/ScrollTracker';
import { WeekendCampaignBanner } from './components/common/WeekendCampaignBanner';
import { WeekendCampaignModal } from './components/common/WeekendCampaignModal';
import { ModalProvider } from './components/common/ModalPortal';
import { useWeekendCampaignModal } from './hooks/useWeekendCampaignModal';
import { SEO } from './components/SEO/SEO';
import { 
  initializeUTMTracking, 
  initializeMicrosoftClarity,
  setClarityTag 
} from './firebaseConfig';
import { logPerformanceReport } from './lib/performanceMonitor';
import { videoPreloader } from './lib/videoPreloader';
import {trackPageView, getAnalyticsStatus } from './lib/analytics';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CancellationRefundPolicy from './pages/CancellationRefundPolicy';
import PricingPage from './pages/PricingPage';
import { NoMoreShadowBaseV } from './components/NoMoreShadow/NoMoreShadow-base';
import AmbassadorProgram from './pages/AmbassadorProgram';
import AmbassadorGuide from './pages/AmbassadorGuide';
import BlogListPage from './pages/BlogListPage';
import BlogDetailPage from './pages/BlogDetailPage';
import { AnalyticsProvider } from './lib/AnalyticsContext';

// Layout component to ensure Footer appears on all pages
const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  return (
    <>
      <SEO />
      {children}
      <Footer />
    </>
  );
};

// Main landing page component
const LandingPageComponent = () => {
  const appRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const [navbarAnimationState, setNavbarAnimationState] = useState('initial');
  const [showBanner, setShowBanner] = useState(false);
  const { isModalOpen, closeModal } = useWeekendCampaignModal();

  // Track page view when the app loads and scroll to top
  useEffect(() => {
    // Initialize analytics on first load
    initializeUTMTracking();
    initializeMicrosoftClarity();
    
    // Set Clarity tags for landing page
    setClarityTag('page_type', 'landing');
    setClarityTag('product', 'mocktrail');
    
    // Track page view with enhanced data using unified analytics
    const pageName = location.pathname === '/' ? 'MockTrail Landing Page' : 
                    `MockTrail Landing - ${location.pathname.replace('/', '')}`;
    
    trackPageView(pageName, location.pathname + location.search);
    
    // Log analytics status for debugging
    const analyticsStatus = getAnalyticsStatus();
    console.log('Analytics Status:', analyticsStatus);
    
    // Initialize performance monitoring
    console.log('🚀 Video Performance Monitoring Active');
    
    // Log performance report every 60 seconds (less frequent)
    const performanceInterval = setInterval(() => {
      logPerformanceReport();
      console.log('📊 Cache Stats:', videoPreloader.getCacheStats());
    }, 60000);
    
    // Log initial performance after 10 seconds
    setTimeout(() => {
      console.log('📈 Initial Performance Check:');
      logPerformanceReport();
    }, 10000);
    
    
    // Force scroll to top on page load/refresh
    window.scrollTo(0, 0);
    
    // Check if there's a hash in the URL and scroll to that section after initial render
    const hash = location.hash.replace('#', '');
    if (hash) {
      // Use setTimeout to ensure DOM is fully loaded
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          const navbarHeight = 100; // Account for fixed navbar
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }
      }, 500);
    }
    
    // Handle history navigation (back/forward buttons)
    window.onpopstate = () => {
      // Short delay to ensure the DOM is ready
      setTimeout(() => {
        // Check if there's a hash in the URL after navigation
        const hash = window.location.hash.replace('#', '');
        if (hash) {
          const element = document.getElementById(hash);
          if (element) {
            const navbarHeight = 100; // Account for fixed navbar
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        } else {
          window.scrollTo(0, 0);
        }
      }, 0);
    };
    
    // Prevent restoring scroll position when navigating back
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    
    return () => {
      window.onpopstate = null;
      clearInterval(performanceInterval);
    };
  }, [location]);

  // Show banner only after navbar is fully expanded
  useEffect(() => {
    if (navbarAnimationState === 'expanded') {
      setTimeout(() => setShowBanner(true), 1200);
    }
  }, [navbarAnimationState]);

  // Handle premium scrolling effects throughout the entire website
  useEffect(() => {
    if (!appRef.current) return;

    // Enable high-performance scrolling with easing
    const enableSmoothScrolling = () => {
      // Get all anchor links
      const anchorLinks = document.querySelectorAll('a[href^="#"]');
      
      // Add smooth scroll behavior to all anchor links
      anchorLinks.forEach((anchor: Element) => {
        anchor.addEventListener('click', (e: Event) => {
          const anchorElement = anchor as HTMLAnchorElement;
          const href = anchorElement.getAttribute('href');
          if (!href) return;
          
          const targetElement = document.querySelector(href);
          if (targetElement) {
            e.preventDefault();
            
            // Get navbar height to adjust scroll position
            const navbar = document.querySelector('.navbar-container');
            const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
            
            // Calculate scroll target position with offset for navbar
            const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - navbarHeight - 20;
            
            // Smooth scroll with cubic-bezier easing
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        });
      });
    };

    // Add reveal animation to sections as they come into view
    const handleRevealAnimations = () => {
      const revealElements = document.querySelectorAll('.reveal-animation');
      const windowHeight = window.innerHeight;
      
      revealElements.forEach((element) => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150; // How much of the element needs to be visible
        
        if (elementTop < windowHeight - elementVisible) {
          element.classList.add('revealed');
        }
      });
    };

    // Enhanced scroll experience with performance optimizations
    const enhanceScrollExperience = () => {
      let ticking = false;
      
      const handleScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            handleRevealAnimations();
            
            // Handle scroll-based effects for premium sections
            const premiumSections = document.querySelectorAll('.premium-scroll-section');
            const scrollTop = window.pageYOffset;
            
            premiumSections.forEach((section) => {
              const sectionTop = (section as HTMLElement).offsetTop;
              const sectionHeight = (section as HTMLElement).offsetHeight;
              const windowHeight = window.innerHeight;
              
              // Calculate if section is in viewport
              const isInViewport = scrollTop < sectionTop + sectionHeight && 
                                  scrollTop + windowHeight > sectionTop;
              
              if (isInViewport) {
                // Apply scroll-based transforms
                const scrollProgress = Math.min(
                  Math.max((scrollTop - sectionTop + windowHeight) / (sectionHeight + windowHeight), 0),
                  1
                );
                
                // Apply premium effects
                const parallaxElements = section.querySelectorAll('.parallax-bg');
                parallaxElements.forEach((element) => {
                  (element as HTMLElement).style.transform = 
                    `translateY(${scrollProgress * 50}px)`;
                });
                
                const blurElements = section.querySelectorAll('.blur-on-scroll');
                blurElements.forEach((element) => {
                  (element as HTMLElement).style.filter = 
                    `blur(${scrollProgress * 3}px)`;
                });
                
                const scaleElements = section.querySelectorAll('.scale-on-scroll');
                scaleElements.forEach((element) => {
                  (element as HTMLElement).style.transform = 
                    `scale(${1 + scrollProgress * 0.1})`;
                });
              }
            });
            
            ticking = false;
          });
          
          ticking = true;
        }
      };
      
      // Add passive scroll listener for better performance
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      // Initial call
      handleScroll();
      
      return () => window.removeEventListener('scroll', handleScroll);
    };

    const initializeAnimations = () => {
      // Initialize smooth scrolling
      enableSmoothScrolling();
      
      // Initialize scroll-based effects
      const removeScrollHandler = enhanceScrollExperience();
      
      // Add intersection observer for better performance on reveal animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      }, observerOptions);
      
      // Observe all reveal animation elements
      const revealElements = document.querySelectorAll('.reveal-animation');
      revealElements.forEach((element) => observer.observe(element));
      
      return () => {
        removeScrollHandler?.();
        observer.disconnect();
      };
    };

    // Initialize all animations with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(initializeAnimations, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div ref={appRef} className="min-h-screen bg-background-light dark:bg-background-dark">
      <ScrollTracker />
      <ParticleBackground />
      <Navbar onAnimationStateChange={setNavbarAnimationState} />
      <WeekendCampaignBanner isBannerVisible={showBanner} />
      <WeekendCampaignModal isOpen={isModalOpen} onClose={closeModal} />
      <Hero />
      <div id="features">
        <ExperienceSection />
        <Features />
      </div>
      <div id="extension">
        <ExtensionShowcase />
      </div>
      <div id="integration"> 
        <NoMoreShadowBaseV />
      </div>
      <div id="how-it-works">
        <HowItWorks />
      </div>
      <div id="testimonials">
        <Testimonials />
      </div>
      <div id="blog">
        <BlogSection />
      </div>
      <div id="faq">
        <FAQ />
      </div>
    </div>
  );
};

// App content with routing
function AppContent() {
  const location = useLocation();

  useEffect(() => {
    // Track page views with unified analytics (Firebase, Meta Pixel, GA4, Clarity)
    const pageName = location.pathname === '/' ? 'Landing Page' : 
                    location.pathname === '/pricing' ? 'Pricing Page' : 
                    `Page: ${location.pathname}`;
    
    trackPageView(pageName,location.pathname);

    // Log analytics status in development
    if (import.meta.env.DEV) {
      console.log('Analytics Status:', getAnalyticsStatus());
    }
  }, [location]);

  return (
    <div className="App min-h-screen bg-gradient-to-br from-background-light via-surface-light to-background-light dark:from-background-dark dark:via-surface-dark dark:to-background-dark">
      <Routes>
        <Route path="/" element={<Layout><LandingPageComponent /></Layout>} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
        <Route path="/terms-of-service" element={<Layout><TermsOfService /></Layout>} />
        <Route path="/cancellation-refund-policy" element={<Layout><CancellationRefundPolicy /></Layout>} />
        <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
        <Route path="/privacy" element={<Layout><PrivacyPolicy /></Layout>} />
        <Route path="/terms" element={<Layout><TermsOfService /></Layout>} />
        <Route path="/cancellation-refund" element={<Layout><CancellationRefundPolicy /></Layout>} />
        <Route path="/ambassador-program" element={<Layout><AmbassadorProgram /></Layout>} />
        <Route path="/ambassador-guide" element={<Layout><AmbassadorGuide /></Layout>} />
        <Route path="/blogs" element={<Layout><BlogListPage /></Layout>} />
        <Route path="/blog/:slug" element={<Layout><BlogDetailPage /></Layout>} />
        {/* Catch-all route for non-existent URLs - redirect to home page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

// Main App component
function App() {
  return (
    <AnalyticsProvider>
      <ModalProvider>
        <Router>
          <AppContent />
        </Router>
      </ModalProvider>
    </AnalyticsProvider>
  );
}

export default App;